import { FC } from 'react';
import { stringFormat, T } from 'src/shared/helpers';
import styled from 'styled-components';

/**
 * The user-facing stages of the "Set as Master Integration" switch journey. The flow is driven by a chain of
 * dialogs and full-page redirects (see useSalesChannelSwitch); each dialog renders the indicator for its own stage,
 * so the seller sees one consistent wizard across the whole journey.
 */
export enum SwitchWizardStep {
    Review = 0,
    // Payment step disabled — the switch flow now skips straight from Review to Subscription.
    // The remaining steps are renumbered so the wizard indicator has no visible gap.
    // Payment = 1,
    Subscription = 1,
    // The product switcher task: created once the new subscription is approved.
    SwitchTask = 2
}

export type SwitchWizardStepCount = 2 | 3;

interface SwitchWizardChannelSettings {
    ecommerceName?: string | null;
    currencyID?: number | null;
}

/** The subscription work remains internal when both non-Shopify integrations use the same currency and API mode. */
export const getSwitchWizardStepCount = (
    master: SwitchWizardChannelSettings | null | undefined,
    secondary: SwitchWizardChannelSettings | null | undefined
): SwitchWizardStepCount => {
    if (!master?.ecommerceName || !secondary?.ecommerceName || master.currencyID == null || secondary.currencyID == null) return 3;

    const bothNonShopifyAndApi =
        master.ecommerceName !== 'SHOPIFY' && master.ecommerceName !== 'API' && secondary.ecommerceName !== 'SHOPIFY' && secondary.ecommerceName !== 'API';
    const sameApiMode = master.ecommerceName === 'API' && secondary.ecommerceName === 'API';
    return (bothNonShopifyAndApi || sameApiMode) && master.currencyID === secondary.currencyID ? 2 : 3;
};

const StepLabel = styled.p`
    margin: 0 0 8px;
    font-size: 12px;
    font-weight: 600;
    color: #6b6b6b;
`;

/** Small "Step X of Y" label shown above each switch dialog's title, in place of the old dotted step path. */
export const SwitchWizardStepIndicator: FC<{ step: SwitchWizardStep; stepCount?: SwitchWizardStepCount }> = ({ step, stepCount = 3 }) => (
    <StepLabel>
        {stringFormat(T._('Step {0} of {1}', 'Admin'), (stepCount === 2 && step !== SwitchWizardStep.Review ? 2 : step + 1).toString(), stepCount.toString())}
    </StepLabel>
);
